<template>
  <div class="py-16 px-24 flex gap-32 justify-center">
    <div v-if="journey">
      <RevTag
        v-if="journey.deliveryTag !== 'UNTRACKED'"
        class="mb-12"
        :label="deliveryTagLabel"
        :variant="deliveryTagVariant"
      />
      <dl>
        <div v-if="hasExpectedDelivery" class="mb-12">
          <dt class="caption block">
            {{ i18n(translations.estimatedDelivery) }}
          </dt>
          <dd class="punchline-sans block">
            {{ formattedEstimatedDeliveryDate }}
          </dd>
          <dd v-if="hasDeliveryDateRange" class="line-through body-1">
            {{ formattedDeliveryDateRange }}
          </dd>
        </div>

        <div class="mb-12">
          <dt class="caption block">
            {{ i18n(translations.deliveryBy) }}
          </dt>
          <dd class="body-1-bold block">{{ journey.carrierName }}</dd>
        </div>

        <div>
          <dt class="caption block">{{ i18n(translations.trackingId) }}</dt>
          <dd class="body-1-bold block">
            <RevLink
              class="inline-flex items-center gap-2 mb-4"
              rel="noopener noreferrer"
              target="_blank"
              :to="journey.trackingUrl"
            >
              {{ journey.trackingNumber }}
              <IconExternalLink size="24" />
            </RevLink>
          </dd>
        </div>
      </dl>

      <div class="my-12">
        <p v-if="isUpdated" class="body-2 mb-4">
          {{ i18n(translations.needHelp) }}
        </p>
        <p class="body-2">
          <FormattedMessage
            :definition="
              isUpdated
                ? translations.manageOrRescheduleDelivery
                : translations.forDeliveryQuestions
            "
          >
            <template #contactCarrierLink>
              <RevLink
                class="inline-flex items-center gap-2"
                rel="noopener noreferrer"
                target="_blank"
                :to="carrierContactUrl"
              >
                {{
                  i18n(translations.contactCarrier, {
                    carrierName: journey.carrierName,
                  })
                }}
                <IconExternalLink size="16" />
              </RevLink>
            </template>
          </FormattedMessage>
        </p>
        <p class="body-2">
          <FormattedMessage
            :definition="
              isUpdated
                ? translations.reportMissingOrDamaged
                : translations.packageMissingOrDamaged
            "
          >
            <template #contactBackMarketLink>
              <RevLink :to="backMarketContactUrl">
                {{ i18n(translations.contactBackMarket) }}
              </RevLink>
            </template>
          </FormattedMessage>
        </p>
      </div>

      <RevInfoBlock
        v-if="isDelayed"
        class="mb-12"
        :content="i18n(translations.delayedAlertDescription)"
        :icon="IconWarning"
        :title="
          i18n(translations.delayedAlertTitle, {
            carrierName: journey.carrierName,
          })
        "
        variant="warning"
      >
        <RevButton
          rel="noopener noreferrer"
          size="small"
          target="_blank"
          :to="journey.trackingUrl"
          variant="primary"
        >
          {{
            i18n(translations.delayedAlertButton, {
              carrierName: journey.carrierName,
            })
          }}
          <IconExternalLink class="inline" size="20" />
        </RevButton>
      </RevInfoBlock>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute, useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'
import { IconExternalLink } from '@ds/icons/IconExternalLink'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Journey } from '~/api/tracking/types'
import { useOrderlineDetails } from '~/scopes/care-commons/components/order/OrderlineCard/composables/useOrderlineDetails'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'

import translations from './TrackParcel.translations'

const route = useRoute()
const $httpFetch = useHttpFetch()
const locale = useI18nLocale()
const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const orderlinePublicId = computed(
  () => route.params.orderlinePublicId as string,
)
const { fetchOrderlineDetails } = useOrderlineDetails()
const orderlineDetailsKey = getAsyncDataKey('orderlineDetails', {
  orderlineId: orderlinePublicId.value,
})

const { data: orderlineDetails } = await useAsyncData(orderlineDetailsKey, () =>
  fetchOrderlineDetails(orderlinePublicId.value),
)

const orderlineId = computed(() => orderlineDetails.value?.orderlineId)

const { data: journeys } = await useAsyncData<Journey[]>(
  `tracking-journey-${orderlinePublicId.value}`,
  () =>
    $httpFetch('/tracking/api/v1/parcels/journeys', {
      query: {
        entityId: orderlinePublicId.value,
        entityType: 'SALES_ORDERLINE',
      },
    }),
)

// Get the first journey (For now the endpoint returns only 1 element)
const journey = computed(() => journeys.value?.[0])

const deliveryTagVariant = computed(() => {
  if (!journey.value) return 'success'

  switch (journey.value.deliveryTag) {
    case 'ON_TRACK':
    case 'DELIVERED':
      return 'success'
    case 'UPDATED':
      return 'warning'
    case 'DELAYED':
      return 'danger'
    default:
      return 'success'
  }
})

const deliveryTagLabel = computed(() => {
  if (!journey.value) return ''

  switch (journey.value.deliveryTag) {
    case 'ON_TRACK':
      return i18n(translations.deliveryOnTrack)
    case 'UPDATED':
      return i18n(translations.updatedDeliveryDate)
    case 'DELAYED':
      return i18n(translations.deliveryDelayed)
    case 'DELIVERED':
      return i18n(translations.delivered)
    default:
      return ''
  }
})

const hasExpectedDelivery = computed(() => {
  if (!journey.value) return false

  const { deliveryTag } = journey.value

  return (
    deliveryTag === 'ON_TRACK' ||
    deliveryTag === 'UPDATED' ||
    deliveryTag === 'UNTRACKED'
  )
})

const formattedEstimatedDeliveryDate = computed(() => {
  if (!journey.value?.estimatedDeliveryDate) return ''

  const date = new Date(journey.value.estimatedDeliveryDate)

  return i18n.date(date, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
})

const hasDeliveryDateRange = computed(() => {
  return (
    journey.value?.promisedMinDeliveryDate &&
    journey.value?.promisedMaxDeliveryDate
  )
})

const formattedDeliveryDateRange = computed(() => {
  if (!hasDeliveryDateRange.value || !journey.value) return ''

  const minDate = new Date(journey.value.promisedMinDeliveryDate)
  const maxDate = new Date(journey.value.promisedMaxDeliveryDate)

  const formatDate = (date: Date) => {
    return i18n.date(date, {
      month: 'long',
      day: 'numeric',
    })
  }

  return `${formatDate(minDate)} - ${formatDate(maxDate)}`
})

const carrierContactUrl = computed(() => {
  return `https://help.backmarket.com/hc/${locale}/categories/360002120993`
})

const backMarketContactUrl = computed(() => {
  if (!orderlineId.value) return ''

  return resolveLocalizedRoute({
    name: CUSTOMER_CARE.DIAGNOSIS.SELF,
    params: {
      orderlineId: orderlineId.value,
    },
  })
})

const isDelayed = computed(() => journey.value?.deliveryTag === 'DELAYED')
const isUpdated = computed(() => journey.value?.deliveryTag === 'UPDATED')
</script>
